import React, { FC, useMemo } from 'react';
import { graphql } from 'gatsby';
import { CampaignPageTypes } from '@shared/types/umbraco';
import { useBazaarVoice } from '@phx-husky/use-bazaarvoice';

import BodyRenderer from 'common/BodyRenderer';
import Layout from 'layout/Layout';
import ScrollToButton from 'components/ScrollToButton';

import { BV_SCRIPT_HOST } from 'utils/constants';

import { elements, bannerElements } from './bodyItems';

import './CampaignPage.scss';
import './CampaignPageOverride.scss';

const CampaignPage: FC<CampaignPageTypes.IProperties> = ({
  pageContext: { breadCrumbs, link, searchUrl, relatedArticlesLinks },
  data: {
    page: {
      nodes: [
        {
          banner,
          defaultCompositions,
          body,
          revisionIds,
          seoMetaKeywords,
          seoMetaTitle,
          seoMetaTitleTemplate,
          seoMetaDescription,
          seoExternalHreflangs,
          seoNoIndex,
          seoCanonicalUrl,
          alternateUrls,
          ogImageUrl,
          disableSubNavigation,
          isScrollToButton,
        },
      ],
    },
    relatedArticles,
    relatedProducts,
    relatedCampaigns,
  },
}) => {
  const { singupFormBaner, waiSettings, siteSettings, header } = defaultCompositions;

  const bvScriptUrl = `${BV_SCRIPT_HOST}/${siteSettings?.bvScript}/bv.js`;
  useBazaarVoice(bvScriptUrl);

  const bannerImage =
    banner?.[0]?.properties?.foregroundImage?.[0]?.properties?.image ||
    banner?.[0]?.properties?.background?.[0]?.properties?.image;

  const imageUrl = ogImageUrl || bannerImage?.fallbackUrl;
  const imageAlt = bannerImage?.imageAlt;
  const isSubNavigationPresent = useMemo(() => {
    const activeNavItem = header?.navigation?.find(
      (item) =>
        item?.properties?.link?.[0] &&
        item.properties.link[0].properties.link[0].url !== '/' &&
        link.includes(item.properties.link[0].properties.link[0].url)
    );

    return !!activeNavItem?.properties?.subNavigation.length;
  }, []);

  return (
    <Layout
      {...{
        ...defaultCompositions,
        seoMetaKeywords,
        seoMetaTitle,
        seoMetaTitleTemplate,
        seoNoIndex,
        seoMetaDescription,
        seoExternalHreflangs,
        seoCanonicalUrl,
        skipText: waiSettings?.skipText,
        link,
        searchUrl,
        alternateUrls,
        imageUrl,
        imageAlt,
        disableSubNavigation,
        isScrollToButton,
      }}
    >
      <BodyRenderer
        bodyData={banner}
        bodyStructure={bannerElements}
        bodyItemProps={{ breadCrumbs, isSubNavigationPresent }}
      />
      {body?.length ? (
        <div className="campaign-page__main-column">
          <BodyRenderer
            bodyData={body}
            bodyStructure={elements}
            bodyItemProps={{
              ...{
                singupFormBaner,
                waiSettings,
                siteSettings,
                revisionIds,
                relatedArticles,
                relatedArticlesLinks,
                relatedProducts,
                usePriceSpider: siteSettings?.usePriceSpider,
                relatedCampaigns,
                isEanProductId: siteSettings?.isEanProductId,
              },
            }}
          />
        </div>
      ) : null}
      {isScrollToButton ? (
        <ScrollToButton
          ariaTop={waiSettings.ariaScrollToTopBtn}
          ariaBottom={waiSettings.ariaScrollToBottomBtn}
        />
      ) : null}
    </Layout>
  );
};

export const query = graphql`
  query allCampaignQuery(
    $link: String = ""
    $relatedArticlesLinks: [String]
    $relatedProductLinks: [String]
    $relatedCampaignsLinks: [String]
  ) {
    page: allCampaign(filter: { link: { eq: $link } }) {
      nodes {
        seoMetaTitle
        seoMetaTitleTemplate
        seoMetaDescription
        seoMetaKeywords
        seoNoIndex
        seoExternalHreflangs {
          key
          value
        }
        seoCanonicalUrl {
          url
        }
        alternateUrls {
          lang
          url
          path
        }
        ogImageUrl
        banner {
          ...FragmentCampaignBanner
        }
        body {
          ... on TImageBanner {
            ...FragmentImageBanner
          }
          ... on TInfoBannerWide {
            ...FragmentWideBanner
          }
          ... on TPartners {
            ...FragmentPartners
          }
          ... on TNewMediaBanner {
            ...FragmentMediaBanner
          }
          ... on TCarousel {
            ...FragmentCustomCarousel
          }
          ... on TImageTextGrid {
            ...FragmenTImageTextGrid
          }
          ... on TDescriptionLinkVideo {
            ...FragmentDescriptionLinkVideo
          }
          ... on TInfoBanner {
            ...FragmentInfoBanner
          }
          ... on TTextSection {
            ...FragmentTextSection
          }
          ... on TInstructionsList {
            ...FragmentInstructionsList
          }
          ... on TSignupPlaceholder {
            ...FragmentSignUp
          }
          ... on TSignUpBlock {
            ...FragmentSignUpBlock
          }
          ... on TImageSection {
            ...FragmentImageSection
          }
          ... on TReceiptForm {
            ...FragmentReceiptForm
          }
          ... on TVideoListSection {
            ...FragmentVideoListSection
          }
          ... on TRelatedArticles {
            ...FragmentRelatedArticles
          }
          ... on TImageMapSection {
            ...FragmentImageMapSection
          }
          ... on TIframeBlock {
            ...FragmentIframeBlock
          }
          ... on TPromoForm {
            ...FragmentPromoForm
          }
          ... on TFAQSection {
            ...FragmentFaqSection
          }
          ... on TSlimbanner {
            ...FragmentSlimBanner
          }
          ... on TDownloadList {
            ...FragmentDownloadList
          }
          ... on TProductListingBanner {
            ...FragmentProductListingBanner
          }
          ... on TCouponsSection {
            ...FragmentCouponsSection
          }
          ... on TPledgeSection {
            ...FragmentPledgeSection
          }
          ... on TVideoBackgroundSection {
            ...FragmentVideoBackgroundSection
          }
          ... on TWaterCalculatorSection {
            ...FragmentWaterCalculator
          }
          ... on TSectionwithcarousel {
            ...FragmentSectionWithCarousel
          }
          ... on TEBook {
            ...FragmentTEBook
          }
          ... on TRelatedCampaigns {
            ...FragmentRelatedCampaigns
          }
          ... on TBazaarvoiceReviews {
            ...FragmentBazaarvoiceReviews
          }
          ... on THomeBanner {
            ...FragmentHomeBanner
          }
          ... on TCommunityConnectSection {
            ...FragmentCommunityConnectSection
          }
          ... on TChallengeBanner {
            ...FragmentChallengeBanner
          }
          ... on TRetailersSection {
            ...FragmentRetailersSection
          }
          ... on TVideo {
            ...FragmentVideoProperties
          }
          ... on TTextBlock {
            ...FragmentTextBlock
          }
          ... on TSuperBowlBanner {
            ...FragmentSuperBowlBanner
          }
          ... on TVoteSection {
            ...FragmentVoteSection
          }
          ... on TChallengeInfoSection {
            ...FragmentChallengeInfoSection
          }
          ... on TWaterWasteBanner {
            ...FragmentWaterWasteBanner
          }
          ... on TWaterWasteVideo {
            ...FragmentWaterWasteVideo
          }
          ... on TSaveWaterProducts {
            ...FragmentSaveWaterProducts
          }
          ... on TRteSection {
            ...FragmentRteSection
          }
          ... on TCampaignCards {
            ...FragmentCampaignCards
          }
          ... on TResiduesSection {
            ...FragmentResiduesSection
          }
          ... on TGuideSection {
            ...FragmentGuideSection
          }
          ... on TCampaignCardsList {
            ...FragmentCampaignCardsList
          }
          ... on THowTheMathWorks {
            ...FragmentHowTheMathWorks
          }
          ... on TWaterChallengeBanner {
            ...FragmentWaterChallengeBanner
          }
          ... on TImageDivider {
            ...FragmentImageDivider
          }
          ... on TLinksSection {
            ...FragmentLinksSection
          }
          ... on TBrandsSection {
            ...FragmentBrandsSection
          }
          ... on TWaterReport {
            ...FragmentWaterReport
          }
          ... on TCompetitionBanner {
            ...FragmentCompetitionBanner
          }
        }
        revisionIds {
          BusinessId
          RevisionId
          ConsentDesc
          MandatoryInd
          ConsentAcceptedInd
          AgreementDate
          ActivityDate
        }
        defaultCompositions {
          footer {
            ...FragmentFooter
          }
          header {
            ...FragmentHeader
          }
          siteSettings {
            ...FragmentSiteSettingsComposition
          }
          brandSettings {
            ...FragmentBrandSettingsComposition
          }
          singupFormBaner {
            ...FragmentSignUpBanner
          }
          waiSettings {
            skipText
            ariaPrev
            ariaNext
            ariaScrollToBottomBtn
            ariaScrollToTopBtn
          }
          warning {
            ...FragmentWarning
          }
          signUpPopup {
            ...FragmentSignUpPopup
          }
          signUpFormPopup {
            ...FragmentSignUpFormPopup
          }
          promoPlatformForm {
            ...FragmentPromoPlatformForm
          }
        }
        disableSubNavigation
        isScrollToButton
      }
    }
    relatedArticles: allArticle(filter: { link: { in: $relatedArticlesLinks } }) {
      nodes {
        link
        tags {
          isCategory
          isLabel
          title
        }
        title
        lang
        date
        dateSufix
        image {
          properties {
            image {
              ...FragmentGatsbyProps
              gatsbyImage {
                childImageSharp {
                  fluid(maxWidth: 300) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            imageAlt
          }
        }
      }
    }
    relatedProducts: allProduct(filter: { link: { in: $relatedProductLinks } }) {
      nodes {
        link
        ean
        tags {
          isCategory
          isFamily
          color {
            label
          }
          isLabel
          title
        }
        title
        shortDescription
        cardDescription
        cardLink {
          url
        }
        images {
          properties {
            imageAlt
            image {
              ...FragmentGatsbyProps
              gatsbyImage {
                childImageSharp {
                  fluid(maxWidth: 200) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
      }
    }
    relatedCampaigns: allCampaign(filter: { link: { in: $relatedCampaignsLinks } }) {
      nodes {
        link
        cardInfo {
          properties {
            description
            image {
              properties {
                image {
                  ...FragmentGatsbyImageSm
                }
                imageAlt
              }
            }
          }
        }
        tags {
          isCategory
          title
        }
      }
    }
  }
`;

export default CampaignPage;
